import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { authService } from '../services/auth.service';

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>(
    'verifying',
  );
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await authService.verifyEmail(token);
        setStatus('success');
        setMessage(response.message);
      } catch (err: any) {
        setStatus('error');
        setMessage(err.message || 'Email verification failed');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Email Verification</h1>

        {status === 'verifying' && (
          <div style={styles.verifying}>
            <div style={styles.spinner} />
            <p style={styles.text}>Verifying your email...</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.success}>
            <div style={styles.successIcon}>✓</div>
            <h3 style={styles.successTitle}>Email Verified!</h3>
            <p style={styles.successText}>
              {message || 'Your email has been successfully verified.'}
            </p>
            <Link to="/login" style={styles.button}>
              Go to Login
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.error}>
            <div style={styles.errorIcon}>✕</div>
            <h3 style={styles.errorTitle}>Verification Failed</h3>
            <p style={styles.errorText}>
              {message || 'The verification link is invalid or has expired.'}
            </p>
            <Link to="/login" style={styles.button}>
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    padding: '1rem',
  },
  card: {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-md)',
    padding: '2rem',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  title: {
    color: 'var(--color-deep-purple)',
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '2rem',
  },
  verifying: {
    padding: '2rem 0',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid var(--color-gray-200)',
    borderTop: '4px solid var(--color-warm-orange)',
    borderRadius: '50%',
    margin: '0 auto 1rem',
    animation: 'spin 1s linear infinite',
  },
  text: {
    color: 'var(--color-gray-600)',
  },
  success: {
    padding: '1rem 0',
  },
  successIcon: {
    width: '60px',
    height: '60px',
    background: 'var(--color-warm-orange)',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    margin: '0 auto 1rem',
  },
  successTitle: {
    color: 'var(--color-deep-purple)',
    marginBottom: '0.5rem',
  },
  successText: {
    color: 'var(--color-dark-navy)',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
  },
  error: {
    padding: '1rem 0',
  },
  errorIcon: {
    width: '60px',
    height: '60px',
    background: '#c33',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
    margin: '0 auto 1rem',
  },
  errorTitle: {
    color: '#c33',
    marginBottom: '0.5rem',
  },
  errorText: {
    color: 'var(--color-dark-navy)',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
  },
  button: {
    display: 'inline-block',
    background: 'var(--color-warm-orange)',
    color: 'white',
    padding: '0.75rem 1.5rem',
    borderRadius: 'var(--radius-md)',
    textDecoration: 'none',
    fontWeight: '600',
  },
};
